import {
  getLinkedTasks,
  sortTasksByDateAndPosition,
  sortTasks,
  isTaskDone,
} from '@lyceo/shared/plan/util';
import { CallState, getError, LoadingState } from '@lyceo/shared/util-loading';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { isSameDay, parseISO } from 'date-fns';
import { selectSelectedDate } from '../plan/plan.selectors';
import { TASK_FEATURE_KEY, TaskState, taskAdapter } from './task.reducer';
import { Task } from '@lyceo/shared/plan/util-models';

export const selectTaskState =
  createFeatureSelector<TaskState>(TASK_FEATURE_KEY);

const { selectAll, selectEntities } = taskAdapter.getSelectors();

export const selectCallState = createSelector(
  selectTaskState,
  (state: TaskState) => state.callState,
);

export const selectListCallState = createSelector(
  selectTaskState,
  (state: TaskState) => state.listCallState,
);

export const selectTaskLoading = createSelector(
  selectCallState,
  (callState) => callState === LoadingState.LOADING,
);

export const selectTaskLoadedList = createSelector(
  selectListCallState,
  (callState) => callState === LoadingState.LOADED,
);

export const selectTaskLoadedError = createSelector(
  selectListCallState,
  (callState) => getError(callState),
);

export const selectError = createSelector(selectCallState, (callState) =>
  getError(callState),
);

export const selectTaskError = createSelector(
  selectError,
  selectTaskLoadedError,
  (error, listError) => error || listError,
);

export const selectAllTasks = createSelector(
  selectTaskState,
  (state: TaskState) => selectAll(state),
);

export const selectTodoIds = createSelector(
  selectTaskState,
  (state: TaskState) => state.todoIds,
);

export const selectAllTodoTasks = createSelector(
  selectAllTasks,
  selectTodoIds,
  (tasks, todoIds) => tasks.filter((task) => todoIds.includes(task.id)),
);

export const selectTodoCallState = createSelector(
  selectTaskState,
  (state: TaskState) => state.todoCallState,
);

export const selectTodoLoaded = createSelector(
  selectTodoCallState,
  (callState: CallState) => callState === LoadingState.LOADED,
);

export const selectAllLinkedTasks = createSelector(selectAllTasks, (tasks) =>
  getLinkedTasks(tasks),
);

export const selectAllCalendarInfoTasks = createSelector(
  selectAllTasks,
  selectAllLinkedTasks,
  selectTodoIds,
  (tasks, linkedTasks, todoTaskIds) => {
    return sortTasks(tasks, false).map((task) => {
      return {
        task,
        linkedTasks: linkedTasks[task.id],
        priority: todoTaskIds.includes(task.id),
        finished: isTaskDone(task),
      };
    });
  },
);

export const selectTasksByDate = createSelector(
  selectSelectedDate,
  selectAllTasks,
  (date, tasks) =>
    tasks.filter((task) => isSameDay(parseISO(task.scheduledAt), date)),
);

export const selectTasksByDateSorted = createSelector(
  selectTasksByDate,
  sortTasks,
);

export const selectTaskEntities = createSelector(
  selectTaskState,
  (state: TaskState) => selectEntities(state),
);

export const selectSelectedId = createSelector(
  selectTaskState,
  (state: TaskState) => state.selectedId,
);

export const selectSelectedTask = createSelector(
  selectTaskEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
);

export const selectSelectedSubtasks = createSelector(
  selectAllTasks,
  selectSelectedId,
  (tasks, selectedId) => {
    if (!selectedId) {
      return [];
    }

    const filtered: Task[] = tasks.filter((task) =>
      task.parentTaskId?.includes(selectedId),
    );

    return sortTasksByDateAndPosition(filtered);
  },
);

export const selectReflectionId = createSelector(
  selectTaskState,
  (state: TaskState) => state.reflectionId,
);

export const selectReflectionTask = createSelector(
  selectTaskEntities,
  selectReflectionId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
);

export const selectTaskModalSubtaskCallState = createSelector(
  selectTaskState,
  (state: TaskState) => state.taskModalSubtaskCallState,
);

export const selectTaskModalSubtaskLoaded = createSelector(
  selectTaskModalSubtaskCallState,
  (callState: CallState) => callState === LoadingState.LOADED,
);

export const selectMoveCallState = createSelector(
  selectTaskState,
  (state: TaskState) => state.moveCallState,
);

export const selectMoveLoading = createSelector(
  selectMoveCallState,
  (callState: CallState) => callState === LoadingState.LOADING,
);

export const selectSortedTasksByDay = (day: Date) =>
  createSelector(selectAllTasks, (tasks) => {
    return sortTasks(
      tasks.filter((task) => isSameDay(task.scheduledAt, day)),
      false,
    );
  });
